import { motion } from "framer-motion";
import React, { forwardRef, Fragment } from "react";

import { Tab } from "@headlessui/react";

import { classed } from "../../classed.config";

const TabButton = classed.button(
  "relative inline-flex items-center gap-1 text-sm focus-visible:outline-hidden",
  "cursor-pointer hover:text-blue-500 text-foreground dark:hover:text-blue-500",
  {
    variants: {
      selected: {
        true: "cursor-default text-blue-500",
      },
      disabled: {
        true: "cursor-not-allowed opacity-50",
      },
      size: {
        sm: "",
        md: "font-medium",
      },
    },
  },
);

type ExtractProps<T> = T extends React.ComponentType<infer P> ? P : T;

type AsProp<EType extends React.ElementType> = {
  as?: EType;
};

type WeldTabProps<EType extends React.ElementType> = ExtractProps<
  typeof Tab
> & {
  icon?: React.ReactNode;
  buttonSize?: "sm" | "md";
  groupId: string;
} & React.PropsWithChildren<AsProp<EType>> &
  React.ComponentPropsWithoutRef<EType>;

type WeldTabComponent = <EType extends React.ElementType = "button">(
  props: WeldTabProps<EType> & { ref?: React.Ref<Element> },
) => React.ReactElement | null;

export const WeldTab = forwardRef(function WeldTab<
  EType extends React.ElementType = "button",
>(
  { icon, buttonSize = "md", as, groupId, ...props }: WeldTabProps<EType>,
  ref: React.Ref<any>,
) {
  return (
    <Tab as={Fragment} {...props}>
      {({ selected }) => (
        <TabButton
          as={as as any}
          ref={ref}
          selected={selected}
          size={buttonSize ?? "md"}
        >
          {icon &&
            React.cloneElement(icon as any, {
              className: "shrink-0 w-4 h-4",
            })}
          {typeof props.children === "function"
            ? props.children({ selected })
            : props.children}

          {selected ? (
            <motion.div
              className="absolute right-0 bottom-[-2px] left-0 h-[2px] rounded-full bg-blue-500"
              layoutId={groupId}
            />
          ) : null}
        </TabButton>
      )}
    </Tab>
  );
}) as WeldTabComponent;
